import { styled } from "goober";
import { extract, type LetterparserMail } from "letterparser";
import { useEffect, useMemo, useState } from "react";
import {
  AttachmentHoverPreview,
  attachmentActionIcon,
} from "../AttachmentHoverPreview";
import { AttachmentLabel, attachmentFileIcon } from "../AttachmentPreviews";
import { CodeEditor } from "../CodeEditor";
import { Icon } from "../Icon";
import { Text } from "../Text";
import type { FileRenderControl } from "./types";

const MAX_PREVIEW_BYTES = 2 * 1024 * 1024;

type Props = {
  url: string;
  bleed?: number;
  onControls?: (controls: FileRenderControl[]) => void;
};

function attachmentBytes(
  content: ArrayBuffer | Uint8Array | string
): Uint8Array {
  if (typeof content === "string") return new TextEncoder().encode(content);
  if (content instanceof Uint8Array) return content;
  return new Uint8Array(content);
}

type Mailbox = NonNullable<LetterparserMail["from"]>;

function formatMailbox(m: Mailbox): string {
  if (m.name && m.address) return `${m.name} <${m.address}>`;
  return m.address || m.name || "";
}

function formatAddresses(addresses: Mailbox[] | undefined): string {
  if (!addresses) return "";
  return addresses.map(formatMailbox).filter(Boolean).join(", ");
}

function formatDate(date: Date | undefined): string {
  return date ? date.toLocaleString() : "";
}

const Container = styled("div")`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const HeaderPanel = styled("div")`
  flex-shrink: 0;
  padding: 16px;
  border-bottom: 0.5px solid var(--border-alpha);
`;

const Subject = styled("div")`
  margin-bottom: 16px;
`;

const Meta = styled("div")`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 3px 16px;
  align-items: baseline;
`;

const MetaValue = styled("div")`
  word-break: break-word;
`;

const Attachments = styled("div")`
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px;
  border-bottom: 0.5px solid var(--border-alpha);
`;

const Body = styled("div")`
  flex: 1;
  min-height: 0;
  position: relative;
`;

const BodyFrame = styled("iframe")`
  width: 100%;
  height: 100%;
  border: none;
  background: white;
`;

const TextBody = styled("div")`
  height: 100%;
  overflow: auto;
  padding: 16px;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: var(--font-sans);
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-primary);
`;

const Empty = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  background: var(--surface-secondary);
  color: var(--text-secondary);
`;

function Field({ label, value }: { label: string; value: string }) {
  return (
    <>
      <Text variant="text-small" color="var(--text-secondary)">
        {label}
      </Text>
      <MetaValue>
        <Text variant="text-small" color="var(--text-primary)">
          {value}
        </Text>
      </MetaValue>
    </>
  );
}

export function Email({ url, bleed, onControls }: Props) {
  const [raw, setRaw] = useState<Uint8Array>();
  const [viewSource, setViewSource] = useState(
    () => localStorage.getItem("emailViewMode") === "source"
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetch(url, { credentials: "include", signal: controller.signal })
      .then(async (res) => {
        if (!res.body) return;
        const reader = res.body.getReader();
        const chunks: Uint8Array[] = [];
        let bytesRead = 0;
        while (bytesRead < MAX_PREVIEW_BYTES) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          bytesRead += value.byteLength;
        }
        void reader.cancel();
        const result = new Uint8Array(bytesRead);
        let offset = 0;
        for (const chunk of chunks) {
          result.set(chunk, offset);
          offset += chunk.byteLength;
        }
        setRaw(result);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [url]);

  const messageText = useMemo(
    () => (raw ? new TextDecoder().decode(raw) : ""),
    [raw]
  );
  const email = useMemo(() => {
    if (!messageText) return null;
    try {
      return extract(messageText);
    } catch {
      return null;
    }
  }, [messageText]);
  const rawText = useMemo(
    () => messageText.replace(/\r\n/g, "\n"),
    [messageText]
  );

  useEffect(() => {
    if (!onControls || !email) return;
    onControls([
      {
        id: "email-source",
        icon: viewSource ? "newspaper" : "code",
        label: viewSource ? "View message" : "View source",
        active: viewSource,
        onToggle: () => {
          setViewSource(!viewSource);
          localStorage.setItem(
            "emailViewMode",
            viewSource ? "rendered" : "source"
          );
        },
      },
    ]);
    return () => onControls([]);
  }, [onControls, email, viewSource]);

  if (!email) return null;

  if (viewSource) {
    return <CodeEditor filePath="output.eml" value={rawText} />;
  }

  const from = email.from ? formatMailbox(email.from) : "";
  const to = formatAddresses(email.to);
  const cc = formatAddresses(email.cc);
  const date = formatDate(email.date);

  return (
    <Container
      style={
        bleed
          ? { margin: -bleed, height: `calc(100% + ${bleed * 2}px)` }
          : undefined
      }
    >
      <HeaderPanel>
        <Subject>
          <Text variant="text-regular-md" color="var(--text-primary)">
            {email.subject || "(no subject)"}
          </Text>
        </Subject>
        <Meta>
          {from && <Field label="From" value={from} />}
          {to && <Field label="To" value={to} />}
          {cc && <Field label="Cc" value={cc} />}
          {date && <Field label="Date" value={date} />}
        </Meta>
      </HeaderPanel>

      {email.attachments && email.attachments.length > 0 && (
        <Attachments>
          {email.attachments.map((attachment, i) => {
            const bytes = attachmentBytes(attachment.body);
            const filename =
              attachment.filename ||
              attachment.contentType.parameters.name ||
              "(unnamed)";
            return (
              <AttachmentHoverPreview
                // oxlint-disable-next-line react/no-array-index-key -- stable, never-reordered list
                key={i}
                fileName={filename}
                mediaType={attachment.contentType.type}
                sizeBytes={bytes.length}
                url={null}
                blob={
                  new Blob([bytes as BlobPart], {
                    type: attachment.contentType.type,
                  })
                }
              >
                <AttachmentLabel type="button">
                  <span className="file-icon">
                    <Icon
                      name={attachmentFileIcon(attachment.contentType.type)}
                      size={10}
                    />
                  </span>
                  <span className="download-icon">
                    <Icon
                      name={attachmentActionIcon(attachment.contentType.type)}
                      size={10}
                    />
                  </span>
                  <Text variant="text-small-md" ellipsis>
                    {filename}
                  </Text>
                </AttachmentLabel>
              </AttachmentHoverPreview>
            );
          })}
        </Attachments>
      )}

      <Body>
        {email.html ? (
          <BodyFrame
            sandbox=""
            srcDoc={email.html}
            title="Email body"
            referrerPolicy="no-referrer"
          />
        ) : email.text ? (
          <TextBody>{email.text}</TextBody>
        ) : (
          <Empty>
            <Text variant="text-small">No message body</Text>
          </Empty>
        )}
      </Body>
    </Container>
  );
}
